import * as React from "react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import OverView from "./OverView";
import StafList from "./StaffList/StafList";
import {
  CircularProgress,
  Container,
  Typography,
  Button,
} from "@mui/material";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import moment from "moment/moment";

import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import AddStaffDialog from "./StaffList/StaffListDialogs/AddStaffDialogs/AddStaffDialog";
import { useQuery } from "@apollo/client";
import {
  GET_STAFF_DETAILS,
} from "../../../apis/organization-apis/people-apis/peopleApis";
import { GET_USER_DETAILS } from "../../../apis/dashboard-apis/dashboardDetailApi";
import {
  GET_AVAILABLE_INTEGRATIONS,
} from "../../../apis/settingsApis/integration-apis/integrationApis";
import IdpPriorityDialog from "../../Settings/Integrations/IdpPriorityDialog/IdpPriorityDialog";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import { MasterLookupItemsType } from "../../../utils/enums";
import { useTheme } from "@mui/material/styles";
import AdminList from "../../../pages/Settings/AdminFlow";
import ExternalUsersList from "./ExternalUsersList/ExternalUsersList";

const TAB_VALUE_TO_KEY = {
  "2": "staff",
  "3": "external-users",
  "4": "admin-management",
};

const TAB_KEY_TO_VALUE = Object.fromEntries(
  Object.entries(TAB_VALUE_TO_KEY).map(([value, key]) => [key, value])
);

const PeopleMain = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { data: staffList, loading, refetch } = useQuery(GET_STAFF_DETAILS, {
    variables: {
      filter: {
        in_scope: true,
      },
      fetchPolicy: "no-cache",
    },
  });
  const { data, refetch: refetchUserDetails } = useQuery(GET_USER_DETAILS);
  const { data: availableIntegrationsData } = useQuery(
    GET_AVAILABLE_INTEGRATIONS
  );
  const [value, setValue] = useState("2");
  const [sourcePriorityOpen, setSourcePriorityOpen] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [name, setName] = useState("Staff");

  // Handle URL params for tabs
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabKeyFromUrl = searchParams.get("tab");

    if (tabKeyFromUrl && TAB_KEY_TO_VALUE[tabKeyFromUrl]) {
      const tabValue = TAB_KEY_TO_VALUE[tabKeyFromUrl];
      if (tabValue !== value) {
        setValue(tabValue);
        // Update name based on tab value
        const tabNames = {
          "2": "Staff",
          "3": "External Users",
          "4": "Admin Management",
        };
        setName(tabNames[tabValue] || "People");
      }
    }
  }, [location.search, value]);

  const [count, setCount] = useState({
    passing: 0,
  });
  const [showIdentityProvider, setShowIntegrationProvider] = useState(false);
  const [hasHrmsIntegration, setHasHrmsIntegration] = useState(false);

  React.useEffect(() => {
    const shouldShowIdentityProviders =
      data?.getAccountDetails?.userIntegrationsDetails?.list.some(
        (integration) => integration.is_active
      );
    setShowIntegrationProvider(shouldShowIdentityProviders);

    const hrmsConnected = data?.getAccountDetails?.userIntegrationsDetails?.hasHrmsIntegration ?? false;
    setHasHrmsIntegration(hrmsConnected);
  }, [data]);

  const idpIntegrationsForPriority = useMemo(() => {
    const enabled =
      data?.getAccountDetails?.userIntegrationsDetails?.list || [];
    const available =
      availableIntegrationsData?.getAvailableIntegrations?.list || [];

    const metaByKey = {};
    available.forEach((int) => {
      metaByKey[int.key] = {
        logo_path: int.logo_path,
        isNativeIdp: int.domains?.some(
          (d) => d.key === MasterLookupItemsType.IDP_PROVIDER
        ),
      };
    });

    return enabled
      .filter((acc) => {
        if (acc.is_active !== true) return false;
        const meta = metaByKey[acc.integration_key];
        return acc.use_as_idp === true || meta?.isNativeIdp;
      })
      .map((acc) => ({
        rciId: acc.id,
        label: acc.integration_label,
        accountLabel: acc.account_label || acc.account_email || null,
        logo_path: metaByKey[acc.integration_key]?.logo_path,
        idpPriority:
          acc.idp_priority === null || acc.idp_priority === undefined
            ? null
            : acc.idp_priority,
      }))
      .filter((i) => i.rciId != null);
  }, [data, availableIntegrationsData]);

  const canSetSourcePriority = idpIntegrationsForPriority.length > 1;

  const showSnackbar = (info) => setSnackbarInfo(info);

  const handleChecks = React.useCallback((daysDifference, countObj) => {
    if (daysDifference < 21) {
      countObj.dueCount += 1;
    }
    if (daysDifference >= 22 && daysDifference <= 30) {
      countObj.criticalCount += 1;
    }
    if (daysDifference > 30) {
      countObj.failingCount += 1;
    }
  }, []);

  const handleDateDifference = React.useCallback((date, difference) => {
    let syncDate =
      typeof date === "number"
        ? moment(Number(date))
        : moment(date, "YYYY-MM-DD");
    let currentDate = moment();
    let dateDifference = currentDate.diff(syncDate, difference);
    return dateDifference;
  }, []);

  React.useEffect(() => {
    if (staffList && staffList.getEmployees) {
      let countObj = {
        passingCount: 0,
        dueCount: 0,
        criticalCount: 0,
        failingCount: 0,
      };
      staffList.getEmployees?.data
        .filter((val) => val.in_scope !== false && !val.termination_date)
        .forEach((item) => {
          if (item.employments !== null && item.manager !== null) {
            countObj.passingCount += 2;
          }
          if (
            Array.isArray(item.employments) &&
            item.employments.length > 0 &&
            !item.manager
          ) {
            countObj.passingCount += 1;
            let differenceInDays = handleDateDifference(
              item.employments[0]?.effective_date,
              "days"
            );
            handleChecks(differenceInDays, countObj);
          }
          if (!item.employments && item.manager) {
            countObj.failingCount += 1;
          }
          if (!item.employments && !item.manager) {
            countObj.failingCount += 2;
          }
        });
      setCount(countObj);
    }
  }, [handleChecks, handleDateDifference, staffList]);



  const handleChange = (event, newValue) => {
    setName(event.target.name);
    setValue(newValue);

    // Update URL with tab parameter
    const searchParams = new URLSearchParams(location.search);
    const tabKey = TAB_VALUE_TO_KEY[newValue] || "staff";
    searchParams.set("tab", tabKey);
    navigate(`/people?${searchParams.toString()}`, { replace: true });
  };

  if (loading) {
    return (
      <Container
        component="main"
        maxWidth="xs"
        sx={{ textAlign: "center", mt: 8 }}
      >
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Box sx={{ width: "100%", typography: "body1" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
        py={2}
      >
        <Typography variant="h4" fontWeight="bold">
          {name}
        </Typography>
        {/* Set source priority — in line with the heading, Staff tab only */}
        {value === "2" && canSetSourcePriority && (
          <Button
            variant="outlined"
            startIcon={<TuneOutlinedIcon sx={{ fontSize: 16 }} />}
            onClick={() => setSourcePriorityOpen(true)}
            sx={{
              fontWeight: 500,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "14px",
              whiteSpace: "nowrap",
              borderColor: theme.customColors?.border?.main,
              color: theme.customColors?.text?.strong,
              "&:hover": {
                borderColor: theme.customColors?.border?.strong,
                backgroundColor: theme.customColors?.background?.hover,
              },
            }}
          >
            People Source Priority
          </Button>
        )}
      </Box>

      <TabContext value={value}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList onChange={handleChange} aria-label="lab API tabs example">
            {/* <Tab label="Overview" value="1" name="Overview" /> */}
            <Tab label="Staff" value="2" name="Staff" />
            <Tab label="External Users" value="3" name="External Users" />
            <Tab label="Admin Management" value="4" name="Admin Management" />
            {/* <Tab label="Security Roles" value="3" name="Security Roles" /> */}
          </TabList>
        </Box>
        <TabPanel value="1" sx={{ pt: 2, width: "100%", pl: 0, pr: 0 }}>
          {staffList ? (
            <OverView
              setValue={setValue}
              staffDetails={staffList}
              count={count}
            />
          ) : (
            "sorry no data available!"
          )}
        </TabPanel>
        <TabPanel value="2" sx={{ pt: 2, width: "100%", pl: 0, pr: 0 }}>
          <Box sx={{ width: "100%" }}>
            {staffList ? (
              <StafList
                data={staffList}
                handleDateDifference={handleDateDifference}
                refetch={refetch}
                loading={loading}
                isTruto={showIdentityProvider}
                hasHrmsIntegration={hasHrmsIntegration}
              />
            ) : (
              "sorry no data available!"
            )}
          </Box>
        </TabPanel>
        <TabPanel value="3" sx={{ pt: 2, width: "100%", pl: 0, pr: 0 }}>
          <ExternalUsersList />
        </TabPanel>
        <TabPanel value="4">
          <AdminList />
        </TabPanel>
        {/* <TabPanel value="3">
          <SecurityRolesList />
        </TabPanel> */}
      </TabContext>
      <AddStaffDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        showIdentityProvider={showIdentityProvider}
        dashboardData={data}
        staffList={staffList}
        refetch={refetch}
      />

      <IdpPriorityDialog
        open={sourcePriorityOpen}
        onClose={() => setSourcePriorityOpen(false)}
        idpIntegrations={idpIntegrationsForPriority}
        onShowSnackbar={showSnackbar}
        hasHrmsIntegration={hasHrmsIntegration}
        onSaved={async () => {
          await refetchUserDetails();
          await refetch({ filter: { in_scope: true } });
        }}
      />

      <ConfirmSnackbar snackbarInfo={snackbarInfo} />
    </Box>
  );
};

export default PeopleMain;
